const FOCUS_TRAP_CAPTURE = true;

const focusTrapStack = [];
let bodyScrollLockCount = 0;
const inertRefCounts = new WeakMap();

function clearModalTrapSubtree(modalElement) {
  if (!modalElement) return;

  for (let i = focusTrapStack.length - 1; i >= 0; i--) {
    const el = focusTrapStack[i];
    if (el === modalElement || modalElement.contains(el)) {
      removeTrapListeners(el);
      delete el._focusTrapPaused;
      focusTrapStack.splice(i, 1);
    }
  }
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

function isActiveTrap(modalElement) {
  return (
    focusTrapStack.length > 0 &&
    focusTrapStack[focusTrapStack.length - 1] === modalElement
  );
}

function removeTrapListeners(modalElement) {
  if (!modalElement) return;

  if (modalElement._focusTrapHandler) {
    document.removeEventListener('keydown', modalElement._focusTrapHandler, FOCUS_TRAP_CAPTURE);
    delete modalElement._focusTrapHandler;
  }

  if (modalElement._focusTrapFocusInHandler) {
    document.removeEventListener('focusin', modalElement._focusTrapFocusInHandler, FOCUS_TRAP_CAPTURE);
    delete modalElement._focusTrapFocusInHandler;
  }

  if (modalElement._focusTrapRafId) {
    cancelAnimationFrame(modalElement._focusTrapRafId);
    delete modalElement._focusTrapRafId;
  }
}

function isElementVisible(el) {
  if (!el || el.disabled) return false;

  let node = el;
  while (node) {
    if (node.getAttribute?.('aria-hidden') === 'true') return false;
    if (node.hasAttribute?.('inert')) return false;

    if (node.nodeType === 1) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }

    node = node.parentElement;
  }

  if (el.matches('input, select, textarea, button, a[href], area[href]')) {
    return true;
  }

  const rect = el.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) return true;

  return el.matches('input, select, textarea, button') && !el.matches('input[type="hidden"]');
}

export function getFocusableElements(modalElement) {
  if (!modalElement) return [];

  return Array.from(modalElement.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (!modalElement.contains(el)) return false;
    if (el.getAttribute('tabindex') === '-1') return false;
    return isElementVisible(el);
  });
}

function focusFirstFocusable(modalElement, initialFocusSelector) {
  const focusable = getFocusableElements(modalElement);
  if (!focusable.length) {
    if (!modalElement.hasAttribute('tabindex')) {
      modalElement.setAttribute('tabindex', '-1');
    }
    modalElement.focus();
    return;
  }

  let toFocus = focusable[0];
  if (initialFocusSelector) {
    const preferred = modalElement.querySelector(initialFocusSelector);
    if (
      preferred &&
      typeof preferred.focus === 'function' &&
      !preferred.disabled &&
      isElementVisible(preferred)
    ) {
      toFocus = preferred;
    }
  }
  toFocus.focus();
}

export function pauseFocusTrap(modalElement) {
  if (!modalElement) return;

  const hadActiveTrap =
    Boolean(modalElement._focusTrapHandler) || focusTrapStack.indexOf(modalElement) !== -1;
  if (!hadActiveTrap) return;

  const stackIndex = focusTrapStack.indexOf(modalElement);
  if (stackIndex !== -1) {
    focusTrapStack.splice(stackIndex, 1);
  }

  modalElement._focusTrapPaused = true;
  removeTrapListeners(modalElement);
}

export function resumeFocusTrap(modalElement) {
  if (!modalElement || !modalElement._focusTrapPaused) return;

  delete modalElement._focusTrapPaused;

  trapFocus(modalElement, {
    previousFocusElement: modalElement._focusTrapPreviousElement,
    skipInitialFocus: true,
  });
}

export function releaseFocusTrap(modalElement, options = {}) {
  if (!modalElement) return;

  const { restoreFocus = true } = options;

  delete modalElement._focusTrapPaused;

  const stackIndex = focusTrapStack.indexOf(modalElement);
  if (stackIndex !== -1) {
    focusTrapStack.splice(stackIndex, 1);
  }

  removeTrapListeners(modalElement);

  const previous = modalElement._focusTrapPreviousElement;
  delete modalElement._focusTrapPreviousElement;

  if (
    restoreFocus &&
    previous &&
    document.body.contains(previous) &&
    typeof previous.focus === 'function'
  ) {
    previous.focus();
  }
}

function railsuiModalRootId(container) {
  let node = container?.parentElement || null;
  while (node) {
    const controllers = (node.getAttribute('data-controller') || '').split(/\s+/);
    if (controllers.includes('railsui-modal')) {
      return node.id || null;
    }
    node = node.parentElement;
  }
  return null;
}

function containerBelongsToRoot(container, rootId) {
  if (!rootId || !container) return false;

  const root = document.getElementById(rootId);
  if (root?.contains(container)) return true;

  return railsuiModalRootId(container) === rootId;
}

export const STACKED_INACTIVE_CLASS = 'aquifer-modal--stacked-inactive';
export const STACKED_FRONT_CLASS = 'aquifer-modal--stacked-front';

function openActiveRailsuiModalContainers(excludeRootId = null) {
  return Array.from(
    document.querySelectorAll("[data-railsui-modal-target='container']:not(.hidden)")
  ).filter((container) => {
    if (container.inert) return false;
    if (container.classList.contains(STACKED_INACTIVE_CLASS)) return false;
    if (excludeRootId && containerBelongsToRoot(container, excludeRootId)) return false;

    return true;
  });
}

function containerZIndex(container) {
  const inline = container.style.zIndex;
  if (inline && inline !== 'auto') {
    const parsed = Number.parseInt(inline, 10);
    if (Number.isFinite(parsed)) return parsed;
  }

  const computed = window.getComputedStyle(container);
  const parsed = Number.parseInt(computed.zIndex, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getNestedModalParent(excludeRootId = null) {
  const openContainers = openActiveRailsuiModalContainers(excludeRootId);
  const stackedFront = openContainers.find((container) =>
    container.classList.contains(STACKED_FRONT_CLASS)
  );
  if (stackedFront) return stackedFront;
  if (openContainers.length > 0) {
    return openContainers.reduce((best, container) => {
      if (!best) return container;
      return containerZIndex(container) >= containerZIndex(best) ? container : best;
    });
  }

  // Legacy shells that are not standard railsui-modal containers.
  const quizModalRoot = document.getElementById('openQuestionContainer');
  if (
    quizModalRoot &&
    excludeRootId !== 'openQuestionContainer' &&
    !quizModalRoot.classList.contains('hidden')
  ) {
    return quizModalRoot;
  }

  const finishCardModal = document.getElementById('finish_card_modal');
  if (finishCardModal && !finishCardModal.classList.contains('hidden')) {
    const style = window.getComputedStyle(finishCardModal);
    if (style.display !== 'none' && style.visibility !== 'hidden') {
      return finishCardModal;
    }
  }

  return null;
}

export function lockStackedParent(parentModal, childModal) {
  if (parentModal) {
    pauseFocusTrap(parentModal);
    if (parentModal.scrollTop > 0) {
      parentModal._stackedScrollTop = parentModal.scrollTop;
    } else if (typeof parentModal._stackedScrollTop !== 'number') {
      parentModal._stackedScrollTop = parentModal.scrollTop;
    }
    parentModal.inert = true;
    parentModal.classList.add(STACKED_INACTIVE_CLASS);
    if (parentModal.id !== 'finish_card_modal') {
      parentModal.setAttribute('aria-hidden', 'true');
    }
  }

  if (childModal) {
    childModal.classList.add('gen-modal--front');
    childModal.classList.add(STACKED_FRONT_CLASS);
  }
}

export function unlockStackedParent(parentModal, childModal) {
  if (childModal) {
    childModal.classList.remove('gen-modal--front');
    childModal.classList.remove(STACKED_FRONT_CLASS);
  }

  if (parentModal) {
    parentModal.inert = false;
    parentModal.classList.remove(STACKED_INACTIVE_CLASS);
    if (parentModal.id !== 'finish_card_modal') {
      parentModal.setAttribute('aria-hidden', 'false');
    }
    if (typeof parentModal._stackedScrollTop === 'number') {
      parentModal.scrollTop = parentModal._stackedScrollTop;
      delete parentModal._stackedScrollTop;
    }
    resumeFocusTrap(parentModal);
  }
}

export function trapNestedModal(parentModal, childModal, options = {}) {
  const { trigger = null, initialFocusSelector = null } = options;

  lockStackedParent(parentModal, parentModal ? childModal : null);

  if (childModal) {
    clearModalTrapSubtree(childModal);
    childModal.classList.remove('hidden');
    childModal.removeAttribute('aria-hidden');
    try {
      childModal.inert = false;
    } catch (e) {
      // Some environments may not support setting `inert`; ignore failures.
    }
    if (!parentModal) {
      childModal.classList.add('gen-modal--front');
    }
  }

  trapFocus(childModal, {
    previousFocusElement: trigger,
    initialFocusSelector,
  });

  return childModal;
}

export function releaseNestedModal(parentModal, childModal, options = {}) {
  const { restoreFocus = true } = options;
  const trigger =
    restoreFocus && childModal?._focusTrapPreviousElement instanceof HTMLElement
      ? childModal._focusTrapPreviousElement
      : null;

  if (childModal) {
    releaseFocusTrap(childModal, { restoreFocus: false });
    clearModalTrapSubtree(childModal);
    childModal.classList.add('hidden');
    childModal.setAttribute('aria-hidden', 'true');
    if (parentModal) {
      try {
        childModal.inert = true;
      } catch (e) {
        // Some environments may not support setting `inert`; ignore failures.
      }
    }
  }

  unlockStackedParent(parentModal, childModal);

  if (
    trigger &&
    document.body.contains(trigger) &&
    typeof trigger.focus === 'function'
  ) {
    trigger.focus();
  }
}

export function lockBodyScroll() {
  bodyScrollLockCount += 1;
  if (bodyScrollLockCount === 1) {
    document.body.classList.add("overflow-hidden");
  }
}

export function unlockBodyScroll() {
  if (bodyScrollLockCount === 0) return;

  bodyScrollLockCount -= 1;
  if (bodyScrollLockCount === 0) {
    document.body.classList.remove("overflow-hidden");
  }
}

const IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS = 'aqui-image-modal-scroll-lock';
const SCROLLBAR_COMPENSATION_VAR = '--railsui-modal-scrollbar-compensation';
let imageModalPageScrollLockCount = 0;

function applyImageModalPageScrollLockClasses() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.classList.add(IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS);
  document.body.classList.add(IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS, 'modal-active');

  if (scrollbarWidth > 0) {
    const value = `${scrollbarWidth}px`;
    document.body.style.paddingRight = value;
    document.documentElement.style.setProperty(SCROLLBAR_COMPENSATION_VAR, value);
  } else {
    document.body.style.removeProperty('padding-right');
    document.documentElement.style.removeProperty(SCROLLBAR_COMPENSATION_VAR);
  }

  document.getElementById('main-content')?.classList.add(IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS);
}

function clearImageModalPageScrollLock() {
  document.documentElement.classList.remove(IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS);
  document.body.classList.remove(IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS, 'modal-active');
  document.body.style.removeProperty('padding-right');
  document.documentElement.style.removeProperty(SCROLLBAR_COMPENSATION_VAR);
  document.getElementById('main-content')?.classList.remove(IMAGE_MODAL_PAGE_SCROLL_LOCK_CLASS);
}

// Image modals lock page scroll via aqui-image-modal-scroll-lock (html/body/#main-content)
// and scrollbar gutter compensation — not lockBodyScroll, so nested library modals keep
// their own overflow-hidden ref count without fighting image open/close.
export function lockImageModalPageScroll() {
  imageModalPageScrollLockCount += 1;
  applyImageModalPageScrollLockClasses();
}

export function unlockImageModalPageScroll() {
  if (imageModalPageScrollLockCount === 0) return;

  imageModalPageScrollLockCount -= 1;
  if (imageModalPageScrollLockCount > 0) return;

  clearImageModalPageScrollLock();
}

export function lockBackground(modalWrapper) {
  const parent = modalWrapper?.parentElement;
  if (!parent) return;
  // Ensure the modal wrapper itself is explicitly interactive (not inert)
  // so tests and environments that don't initialize the property get a
  // defined boolean value instead of `undefined`.
  try {
    modalWrapper.inert = false;
  } catch (e) {
    // Some environments may not support setting `inert`; ignore failures.
  }

  Array.from(parent.children).forEach((child) => {
    if (child === modalWrapper) return;

    const count = inertRefCounts.get(child) || 0;
    inertRefCounts.set(child, count + 1);
    child.inert = true;
  });
}

export function unlockBackground(modalWrapper) {
  const parent = modalWrapper?.parentElement;
  if (!parent) return;

  Array.from(parent.children).forEach((child) => {
    if (child === modalWrapper) return;

    const count = inertRefCounts.get(child) || 0;
    if (count <= 1) {
      inertRefCounts.delete(child);
      child.inert = false;
    } else {
      inertRefCounts.set(child, count - 1);
    }
  });
}

export function trapFocus(modalElement, options = {}) {
  if (!modalElement) return;

  const {
    initialFocusSelector,
    previousFocusElement = null,
    skipInitialFocus = false,
  } = options;

  removeTrapListeners(modalElement);

  const existingIndex = focusTrapStack.indexOf(modalElement);
  if (existingIndex !== -1) {
    focusTrapStack.splice(existingIndex, 1);
  }
  focusTrapStack.push(modalElement);

  delete modalElement._focusTrapPaused;

  const active = document.activeElement;
  modalElement._focusTrapPreviousElement =
    previousFocusElement ||
    (active instanceof HTMLElement && active !== document.body ? active : null);

  function handleTrap(e) {
    if (!isActiveTrap(modalElement)) return;
    if (e.key !== 'Tab') return;

    const focusable = getFocusableElements(modalElement);
    const current = document.activeElement;

    if (!focusable.length) {
      e.preventDefault();
      modalElement.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!modalElement.contains(current)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }

    const idx = focusable.indexOf(current);
    if (idx === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }

    e.preventDefault();
    const nextIndex = e.shiftKey
      ? (idx === 0 ? focusable.length - 1 : idx - 1)
      : (idx === focusable.length - 1 ? 0 : idx + 1);
    focusable[nextIndex].focus();
  }

  function handleFocusIn(e) {
    if (!isActiveTrap(modalElement)) return;
    if (!modalElement._focusTrapHandler) return;
    if (modalElement.contains(e.target)) return;

    const focusable = getFocusableElements(modalElement);
    if (focusable.length) {
      focusable[0].focus();
    } else {
      modalElement.focus();
    }
  }

  document.addEventListener('keydown', handleTrap, FOCUS_TRAP_CAPTURE);
  document.addEventListener('focusin', handleFocusIn, FOCUS_TRAP_CAPTURE);

  modalElement._focusTrapHandler = handleTrap;
  modalElement._focusTrapFocusInHandler = handleFocusIn;

  if (!skipInitialFocus) {
    modalElement._focusTrapRafId = requestAnimationFrame(() => {
      delete modalElement._focusTrapRafId;
      if (!isActiveTrap(modalElement)) return;
      focusFirstFocusable(modalElement, initialFocusSelector);
    });
  }
}

export function enableBackgroundInert(excludeElement) {
  const inertedElements = [];

  for (const child of document.body.children) {
    if (child !== excludeElement) {
      child.inert = true;
      inertedElements.push(child);
    }
  }

  return inertedElements;
}

export function disableBackgroundInert(inertedElements) {
  if (!inertedElements?.length) return;

  for (const element of inertedElements) {
    element.inert = false;
  }
}
